/**
 * «الان وقتش هست که اجازه‌ی اعلانِ مرورگر را بپرسیم؟»
 *
 * منطقِ خالص، بدونِ هیچ APIِ مرورگر — پس با `npm test` آزموده می‌شود و هر
 * دو تصمیمِ سختش در یک جا می‌ماند. خودِ گرفتنِ توکن در
 * `apps/web/src/features/notifications/webPush.ts` است.
 *
 * ═══ ⚠ چرا اصلاً «کارت» لازم است و مستقیم نمی‌پرسیم ═══
 *
 * مرورگر اجازه‌ی پرسیدن را **یک بار** می‌دهد. کاربری که یک بار «مسدود کن»
 * را زد، دیگر هیچ کدی نمی‌تواند دوباره بپرسد — نه امروز، نه هرگز؛ فقط
 * خودش از تنظیماتِ سایت می‌تواند برش گرداند. پس پنجره‌ی خودِ مرورگر
 * تنها بعد از یک «بله»ی آگاهانه باز می‌شود، وگرنه یک «نه»ی شتاب‌زده
 * تنها فرصت را برای همیشه می‌سوزاند.
 *
 * ═══ ⚠ کسی که اپ را دارد، پرسیده نمی‌شود ═══
 *
 * اعلانِ مرورگر بدل است، نه ارتقا: فقط تا وقتی کار می‌کند که مرورگر (یا
 * سرویس‌ورکرش) زنده باشد، و روی iOS اصلاً بدونِ افزودن به صفحه‌ی خانه
 * وجود ندارد. کسی که اپِ گوشی را نصب کرده همین حالا راهِ بهترش را دارد و
 * پرسیدن از او فقط یک مزاحمت است. نشانه‌اش هم حدس نیست: یک ردیفِ
 * `push_tokens` با پلتفرمِ `android`/`ios`.
 */

/** حالتِ اجازه، به‌علاوه‌ی حالتی که خودِ مرورگر بلد نیست. */
export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/** «بعداً» یعنی این‌قدر سکوت. یک هفته: کوتاه‌تر مزاحمت است، بلندتر فراموشی. */
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export interface OfferInput {
  signedIn: boolean;
  permission: PushPermission;
  /** این حساب روی گوشی هم توکن دارد؟ نگاه کن به بالا. */
  hasNativeToken: boolean;
  /**
   * اولین قدمِ واقعی‌اش را برداشته؟ — سیگنالی ساخته، درخواستِ دوستی
   * فرستاده، یا دوستی‌ای را قبول کرده (قرارِ WHOSUP-71، تصمیمِ ۲).
   *
   * ⚠ تازه‌واردی که هنوز هیچ‌کدام را نکرده نباید سرِ اولین ورود کارت ببیند:
   * اعلان برای کسی معنا دارد که چیزی برای خبردار شدن دارد. صفحه‌ی تنظیماتِ
   * اعلان‌ها استثناست — رفتن به آن‌جا خودش قصدِ آگاهانه است و `true`
   * می‌فرستد.
   */
  hasFirstActivity: boolean;
  /** پایانِ «بعداً»، یا `null` اگر هیچ‌وقت زده نشده. `Infinity` یعنی «هرگز». */
  snoozedUntil: number | null;
  now: number;
  /** کلیدِ VAPID تنظیم نشده باشد، اصلاً چیزی برای روشن کردن نیست. */
  configured: boolean;
}

export function shouldOfferPush(i: OfferInput): boolean {
  if (!i.configured) return false;
  if (!i.signedIn) return false;
  // `granted` یعنی از قبل روشن است، `denied` یعنی مرورگر دیگر نمی‌گذارد
  // بپرسیم و کارت فقط یک دکمه‌ی بی‌اثر می‌شد.
  if (i.permission !== 'default') return false;
  if (i.hasNativeToken) return false;
  if (!i.hasFirstActivity) return false;
  if (i.snoozedUntil !== null && i.now < i.snoozedUntil) return false;
  return true;
}

/*
 * بخشِ «فاصله‌ی پرسیدن» از این‌جا رفت به `core/lib/notifPolling.ts`.
 *
 * دلیلش: موبایل هم به همان `pollInterval()` نیاز پیدا کرد، و ماندنش در
 * فایلی به نامِ «پیشنهادِ پوشِ **وب**» یعنی هر خواننده‌ی سمتِ موبایل باید
 * یک بار تعجب کند. این فایل حالا فقط دربارهٔ کارتِ پیشنهادِ وب است.
 */
